import { state, settings } from './state';
import { $ } from './dom';
import { t } from './i18n';
import {
  applyMarkdownEnhancers,
  getActiveConversationSession,
  isGenerationActive,
  removeSessionAttachmentViaHook,
} from './app-hooks';
import { extractMathSpans, restoreMathSpans } from './math';
import type { AttachedDoc, CategoryData, ToastOptions } from './types';

// --- Provider capabilities cache for web_search support ---
const webSearchProviders = new Set<string>();
let providersLoaded = false;

export async function loadProviderCapabilities(): Promise<void> {
  if (providersLoaded) return;
  try {
    const headers: Record<string, string> = {};
    if (state.authToken) headers['Authorization'] = 'Bearer ' + state.authToken;
    const r = await fetch('/api/providers', { headers });
    if (r.ok) {
      const providers = await r.json() as Array<{ id: string; capabilities?: Record<string, unknown> }>;
      webSearchProviders.clear();
      for (const p of providers) {
        if (p.capabilities?.web_search === true) {
          webSearchProviders.add(p.id);
        }
      }
      providersLoaded = true;
      updateWebSearchToggle();
    }
  } catch (e) {
    console.error('Failed to load provider capabilities:', e);
  }
}

export function updateWebSearchToggle(): void {
  const toggle = document.querySelector<HTMLInputElement>('#feat-websearch');
  if (!toggle) return;
  const label = toggle.closest('.toggle-option') as HTMLElement | null;

  // WWW-поиск заблокирован для пользователя
  toggle.disabled = true;
  toggle.checked = false;

  if (label) {
    label.classList.add('is-disabled');
    label.title = state.lang === 'ru'
      ? 'WWW-поиск отключен'
      : 'Web search is disabled';
  }
}

/** Копирование с запасным путём: navigator.clipboard живёт только в secure context,
 *  а стенд открывают и по http://<ip> — там остаётся execCommand. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* падаем в запасной путь */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/* Тост стоит внизу, но не над строкой ввода: раньше он ложился ровно на поле и
   кнопки отправки/микрофона, поэтому его и унесли наверх. Теперь низ считается от
   верхней кромки строки ввода активного экрана — сообщение внизу, а органы
   управления свободны. */
const TOAST_ANCHORS = ['#input-wrapper', '.adlg-input-row'];
const TOAST_ANCHOR_GAP = 12;

/** Снятие показанного сейчас сообщения — вместе с его слушателями window. */
let currentDismiss: (() => void) | null = null;

function toastBottomOffset(): number {
  let anchorTop = Infinity;
  for (const selector of TOAST_ANCHORS) {
    document.querySelectorAll<HTMLElement>(selector).forEach(el => {
      const rect = el.getBoundingClientRect();
      // Скрытая вкладка (display:none) даёт нулевой прямоугольник — она не якорь.
      if (rect.width <= 0 || rect.height <= 0) return;
      if (rect.top >= window.innerHeight || rect.bottom <= 0) return;
      if (rect.top < anchorTop) anchorTop = rect.top;
    });
  }
  if (anchorTop === Infinity) return 0;
  const offset = window.innerHeight - anchorTop + TOAST_ANCHOR_GAP;
  // Высокая строка ввода не должна выталкивать тост за верх экрана.
  return Math.max(0, Math.min(offset, window.innerHeight * 0.6));
}

export function showToast(msg: string, options: ToastOptions | string = {}) {
  if (typeof options === 'string') {
    options = { variant: options };
  }
  const variant = options.variant || 'success';
  const defaultDuration = variant === 'error' ? 10000 : 5000;
  const duration = options.duration != null ? Number(options.duration) : defaultDuration;

  // Предыдущее сообщение снимаем его же процедурой: она снимает и слушателей
  // перерасчёта позиции. Просто remove() оставил бы их висеть на window.
  if (currentDismiss) currentDismiss();
  document.querySelector('.toast')?.remove();

  const toast = document.createElement('div');
  toast.className = variant === 'success' ? 'toast' : `toast toast--${variant}`;

  const text = document.createElement('div');
  text.className = 'toast-text';
  text.textContent = msg;

  const actions = document.createElement('div');
  actions.className = 'toast-actions';

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'toast-btn toast-copy';
  copyBtn.textContent = t('copy');
  copyBtn.title = t('toast_copy');
  copyBtn.setAttribute('aria-label', t('toast_copy'));

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'toast-btn toast-close';
  closeBtn.textContent = '×';
  closeBtn.title = t('toast_close');
  closeBtn.setAttribute('aria-label', t('toast_close'));

  actions.append(copyBtn, closeBtn);
  toast.append(text, actions);
  document.body.appendChild(toast);

  const reposition = () => {
    const offset = toastBottomOffset();
    toast.style.bottom = offset > 0 ? `${offset}px` : '';
  };
  reposition();

  // Уход по таймеру и уход по анимации заводятся из одной точки, иначе после
  // паузы тост успевал стать прозрачным, но ещё висел в разметке.
  const applyFade = (ms: number, withIntro: boolean) => {
    const fade = `toastOut var(--transition-slow) ${Math.max(0, ms - 500)}ms forwards`;
    toast.style.animation = withIntro ? `toastIn var(--transition-slow), ${fade}` : fade;
    toast.style.animationPlayState = 'running';
  };
  applyFade(duration, true);

  let timer: number | null = null;
  let remaining = duration;
  let startedAt = performance.now();

  const dismiss = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
    window.removeEventListener('resize', reposition);
    window.visualViewport?.removeEventListener('resize', reposition);
    if (currentDismiss === dismiss) currentDismiss = null;
    toast.remove();
  };
  currentDismiss = dismiss;

  // Пока курсор или фокус на сообщении, отсчёт стоит: иначе текст не успеть
  // прочитать и скопировать.
  const pause = () => {
    if (timer == null) return;
    clearTimeout(timer);
    timer = null;
    remaining -= performance.now() - startedAt;
    toast.style.animationPlayState = 'paused';
  };
  const resume = () => {
    if (timer != null) return;
    // Отпустили — даём дочитать хотя бы полторы секунды.
    remaining = Math.max(1500, remaining);
    startedAt = performance.now();
    applyFade(remaining, false);
    timer = window.setTimeout(dismiss, remaining);
  };

  copyBtn.addEventListener('click', () => {
    void copyToClipboard(msg).then(ok => {
      copyBtn.textContent = ok ? t('copied') : t('toast_copy_failed');
      copyBtn.classList.toggle('is-done', ok);
      setTimeout(() => {
        copyBtn.textContent = t('copy');
        copyBtn.classList.remove('is-done');
      }, 1500);
    });
  });
  closeBtn.addEventListener('click', dismiss);
  toast.addEventListener('mouseenter', pause);
  toast.addEventListener('mouseleave', resume);
  toast.addEventListener('focusin', pause);
  toast.addEventListener('focusout', resume);
  window.addEventListener('resize', reposition);
  window.visualViewport?.addEventListener('resize', reposition);

  timer = window.setTimeout(dismiss, duration);
}

export function estimateTokens(text: string) { return Math.ceil((text || '').length / 3.5); }
export function getTotalDocTokens() {
  const session = getActiveConversationSession();
  if (!session) return 0;
  return session.attachedDocs.reduce((s, d) => s + (d.sessionScoped ? 0 : (d.tokens || 0)), 0);
}

function escapeHtml(value: string) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function sanitizeHtml(value: string) {
  return typeof window.DOMPurify !== 'undefined' ? DOMPurify.sanitize(value) : escapeHtml(value);
}

/**
 * Markdown → безопасный HTML. Без библиотек — экранированный текст с переносами.
 *
 * Формулы вынимаются ДО markdown и возвращаются ПОСЛЕ санитайзера: между этими
 * двумя точками TeX не выживает — ни `\[…\]`, ни MathML от KaTeX (shared/math).
 */
export function renderMarkdownHtml(value: string) {
  const { text, spans } = extractMathSpans(value);
  if (typeof window.marked !== 'undefined' && typeof window.DOMPurify !== 'undefined') {
    return restoreMathSpans(DOMPurify.sanitize(marked.parse(text) as string), spans);
  }
  return restoreMathSpans(escapeHtml(text).replace(/\n/g, '<br>'), spans);
}

export function updateContextBadge() {
  const sysTokens = estimateTokens(settings.system_prompt);
  const docTokens = getTotalDocTokens();
  const session = getActiveConversationSession();
  const conversationTokens = (session?.history || []).reduce((s, m) => s + estimateTokens(m.content), 0);
  const total = sysTokens + docTokens + conversationTokens;
  const pct = Math.min(100, Math.round((total / state.contextSize) * 100));

  const icon = pct < 60 ? '🟢' : pct < 85 ? '🟡' : '🔴';
  const contextUsage = $('context-usage');
  const contextBadge = $('context-badge');
  const sendBtn = $<HTMLButtonElement>('send-btn');

  if (contextUsage) contextUsage.textContent = `${icon} ${pct}%`;
  if (contextBadge) {
    contextBadge.title = `Использовано ${total} из ${state.contextSize} токенов`;
    contextBadge.classList.remove('warn', 'danger');
  }

  const isOverLimit = total > state.contextSize;

  if (isOverLimit) {
    if (contextBadge) contextBadge.classList.add('danger');
    if (contextBadge) contextBadge.title = state.lang === 'ru' ? 'Превышен лимит контекста!' : 'Context limit exceeded!';
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.title = state.lang === 'ru' ? 'Слишком много текста' : 'Too much text';
    }
  } else {
    if (sendBtn) {
      const generating = isGenerationActive();
      sendBtn.disabled = generating;
      sendBtn.title = state.lang === 'ru' ? 'Отправить' : 'Send';
    }
    if (pct >= 85 && contextBadge) contextBadge.classList.add('warn');
  }
}

export function updateTokenInfo() {
  const docTokens = getTotalDocTokens();
  const tokenInfo = $('token-info');
  if (!tokenInfo) return;
  if (docTokens > 0) {
    tokenInfo.textContent = `📎 ~${docTokens} tokens`;
  } else {
    tokenInfo.textContent = '';
  }
}

export function setWelcomeVisible(isVisible: boolean) {
  $('welcome-screen')?.classList.toggle('hidden', !isVisible);
  $('view-conversation')?.classList.toggle('welcome-active', isVisible);
}

export function autoResizeTextarea() {
  const userInput = $<HTMLTextAreaElement>('user-input');
  if (!userInput) return;
  const viewportLimit = Math.max(72, Math.floor(window.innerHeight * 0.22));
  const maxHeight = Math.min(160, viewportLimit);
  userInput.style.height = 'auto';
  userInput.style.height = Math.min(userInput.scrollHeight, maxHeight) + 'px';
}

function preprocessText(text: string) {
  if (text.trim().startsWith('{') || text.includes('"answer"')) {
    const answerMatch = text.match(/"answer"\s*:\s*"([\s\S]*?)"/);
    if (answerMatch) {
      text = answerMatch[1];
    } else {
      const partialMatch = text.match(/"answer"\s*:\s*"([\s\S]*)$/);
      if (partialMatch) text = partialMatch[1];
    }
    text = text.replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  return text;
}

function buildHtml(text: string) {
  let thinkContent = '';
  let mainContent = text;
  const thinkMatch = text.match(/<think>([\s\S]*?)(<\/think>|$)/);
  if (thinkMatch) {
    thinkContent = thinkMatch[1].trim();
    mainContent = text.replace(/<think>[\s\S]*?(<\/think>|$)/, '').trim();
  }

  const contextRegex = /<context_boundary>([\s\S]*?)(?:<\/context_boundary>|$)/g;
  const contextBlocks: string[] = [];
  mainContent = mainContent.replace(contextRegex, (_, content: string) => {
    contextBlocks.push(content.trim());
    return '';
  });

  const toolRegex = /<tool name="([\s\S]*?)">([\s\S]*?)(<\/tool>|$)/g;
  mainContent = mainContent.replace(toolRegex, (_match, name: string, _content: string, closed: string) => {
    const isDone = closed === '</tool>';
    const displayName = name === 'web_search' ? (state.lang === 'ru' ? 'Поиск в сети' : 'Web Search') : name;
    const statusText = isDone
      ? (state.lang === 'ru' ? 'готово' : 'done')
      : (state.lang === 'ru' ? 'выполняется...' : 'running...');
    return `<div class="tool-block ${isDone ? 'done' : ''}"><div class="tool-header"><span class="tool-icon">${isDone ? '✅' : '🔧'}</span><span class="tool-name">${sanitizeHtml(displayName)}</span><span class="tool-status">${sanitizeHtml(statusText)}</span></div></div>`;
  });

  let html = '';
  if (thinkContent) {
    html += `<div class="think-block"><button class="think-toggle" aria-expanded="false" aria-label="Показать/скрыть размышление">💭 ${t('thinking')}… <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg></button><div class="think-content">${renderMarkdownHtml(thinkContent)}</div></div>`;
  }
  if (mainContent) html += renderMarkdownHtml(mainContent);
  if (contextBlocks.length > 0) {
    html += `<div class="citations-block"><strong>Источники контекста:</strong><ul>${contextBlocks.map(c => `<li>${sanitizeHtml(c).replace(/\\n/g, '<br>')}</li>`).join('')}</ul></div>`;
  }
  return { html, mainContent };
}

export function renderMarkdown(el: HTMLElement, text: string) {
  text = preprocessText(text);
  const { html, mainContent } = buildHtml(text);

  el.dataset.raw = mainContent;
  el.innerHTML = html;
  applyMarkdownEnhancers(el);

  el.querySelectorAll('.think-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const content = btn.nextElementSibling;
      if (!content) return;
      const isOpen = content.classList.toggle('open');
      btn.setAttribute('aria-expanded', String(isOpen));
    });
  });

  el.querySelectorAll('pre code:not([data-hl])').forEach(block => {
    const codeBlock = block as HTMLElement;
    if (window.hljs) hljs.highlightElement(codeBlock);
    codeBlock.dataset.hl = '1';
    const pre = codeBlock.parentElement;
    if (!pre || pre.querySelector('.copy-code-btn')) return;
    const btn = document.createElement('button');
    btn.className = 'copy-code-btn';
    btn.textContent = t('copy');
    btn.setAttribute('aria-label', t('copy'));
    btn.addEventListener('click', () => {
      void copyToClipboard(codeBlock.textContent || '').then(ok => {
        btn.textContent = ok ? '✓' : t('toast_copy_failed');
        setTimeout(() => { btn.textContent = t('copy'); }, 1500);
      });
    });
    pre.appendChild(btn);
  });
}

export function showTypingIndicator(el: HTMLElement) {
  el.innerHTML = `
    <div class="styled-delay-indicator">
      <span class="delay-dots">.</span>
      <span class="delay-timer">0с</span>
    </div>
  `;

  const timerEl = el.querySelector('.delay-timer') as HTMLElement | null;
  const dotsEl = el.querySelector('.delay-dots') as HTMLElement | null;
  if (!timerEl || !dotsEl) return;

  const startTime = Date.now();
  const intervalId = setInterval(() => {
    if (!document.body.contains(el) || !el.querySelector('.styled-delay-indicator')) {
      clearInterval(intervalId);
      return;
    }
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const unit = state.lang === 'ru' ? 'с' : 's';
    timerEl.textContent = `${elapsed}${unit}`;

    // Анимация бегущих точек (от 1 до 3 точек, сменяются каждые 350 мс)
    const dotsCount = (Math.floor((Date.now() - startTime) / 350) % 3) + 1;
    dotsEl.textContent = '.'.repeat(dotsCount).padEnd(3, '\u00A0');
  }, 100);
}

export function renderDocChip(docOrName: AttachedDoc | string, tokensOrIndex: number, sessionId?: string, indexMaybe?: number) {
  const container = $('attached-docs');
  if (!container) return;

  let doc: AttachedDoc;
  let index: number;
  if (typeof docOrName === 'object' && docOrName !== null) {
    doc = docOrName;
    index = tokensOrIndex;
  } else {
    doc = { name: docOrName, tokens: tokensOrIndex };
    index = indexMaybe ?? 0;
  }

  const chip = document.createElement('div');
  chip.className = 'doc-chip';
  const statusHtml = doc.status
    ? `<span class="doc-chip-status doc-chip-status--${doc.status === 'ready' ? 'ready' : (doc.status === 'failed' ? 'failed' : 'pending')}">${doc.status === 'pending' || doc.status === 'processing' ? 'индексируется…' : (doc.status === 'ready' ? 'готов' : 'ошибка')}</span>`
    : (doc.tokens != null ? `(~${doc.tokens})` : '');
  chip.innerHTML = `<span>📎 ${doc.name} ${statusHtml}</span>
                    <button class="remove-doc-btn" data-index="${index}">×</button>`;
  container.appendChild(chip);
  const sid = sessionId || getActiveConversationSession()?.id;
  chip.querySelector('.remove-doc-btn')?.addEventListener('click', () => {
    if (!sid || !removeSessionAttachmentViaHook(sid, index)) {
      removeDoc(index);
    }
  });
}

export function removeDoc(index: number) {
  const session = getActiveConversationSession();
  if (!session) return;
  session.attachedDocs.splice(index, 1);
  const container = $('attached-docs');
  if (container) {
    container.textContent = '';
    session.attachedDocs.forEach((d, i) => renderDocChip(d, i));
  }
  updateTokenInfo();
  updateContextBadge();
}

function getSuggestedQuestions(categoryData: CategoryData | undefined) {
  if (!categoryData || !categoryData.suggested_questions || !categoryData.suggested_questions.trim()) {
    return [];
  }
  return categoryData.suggested_questions.split('\n')
    .map(q => q.trim())
    .filter(q => q.length > 0);
}

function handleHintClick(promptVal: string) {
  const userInput = $<HTMLTextAreaElement>('user-input');
  if (userInput) {
    userInput.value = promptVal;
    userInput.focus();
    autoResizeTextarea();
  }
}

function getQuestionIcon(text: string): string {
  const lowercase = text.toLowerCase();
  
  if (
    lowercase.includes('код') || lowercase.includes('программ') || lowercase.includes('скрипт') || 
    lowercase.includes('разработ') || lowercase.includes('ошибк') || lowercase.includes('code') || 
    lowercase.includes('develop') || lowercase.includes('bug')
  ) {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hint-icon"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`;
  }
  if (
    lowercase.includes('документ') || lowercase.includes('файл') || lowercase.includes('книг') || 
    lowercase.includes('pdf') || lowercase.includes('doc') || lowercase.includes('file') || 
    lowercase.includes('анализ') || lowercase.includes('summary')
  ) {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hint-icon"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;
  }
  if (
    lowercase.includes('текст') || lowercase.includes('напиши') || lowercase.includes('письмо') || 
    lowercase.includes('стать') || lowercase.includes('write') || lowercase.includes('text') || 
    lowercase.includes('email') || lowercase.includes('создай')
  ) {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hint-icon"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>`;
  }
  if (
    lowercase.includes('перевод') || lowercase.includes('язык') || lowercase.includes('словар') || 
    lowercase.includes('translate') || lowercase.includes('lang')
  ) {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hint-icon"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>`;
  }
  return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hint-icon"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
}

function createHintChip(displayText: string, onClick: () => void, delayIndex?: number) {
  const btn = document.createElement('button');
  btn.className = 'hint-chip';
  btn.style.opacity = '0';
  btn.addEventListener('click', onClick);
  btn.style.animation = 'slideUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards';
  if (delayIndex !== undefined) {
    btn.style.animationDelay = `${delayIndex * 0.05}s`;
  }
  
  const cleanText = displayText.replace(/^([\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}]|💡|✍️|📄|🌐)\s*/u, '');
  const iconHtml = getQuestionIcon(displayText);
  btn.innerHTML = `${iconHtml}<span>${sanitizeHtml(cleanText)}</span>`;
  return btn;
}

function renderHints(container: HTMLElement, questions: string[]) {
  container.textContent = '';

  if (questions.length > 0) {
    questions.forEach((q, i) => {
      const btn = createHintChip(q, () => handleHintClick(q), i);
      container.appendChild(btn);
    });
    return;
  }

  const staticHints = [
    { key: 'explain_code', i18nKey: 'hint_code', fallback: '💡 Помоги разобраться' },
    { key: 'write_text', i18nKey: 'hint_text', fallback: '✍️ Напиши текст' },
    { key: 'analyze_doc', i18nKey: 'hint_doc', fallback: '📄 Анализ документа' },
    { key: 'translate', i18nKey: 'hint_translate', fallback: '🌐 Переведи' }
  ];

  staticHints.forEach((hint, i) => {
    let displayText = hint.fallback;
    if (typeof t === 'function') {
      const i18nText = t(hint.i18nKey);
      if (i18nText && i18nText !== hint.i18nKey) {
        displayText = i18nText;
      }
    }

    const promptVal = typeof t === 'function' ? t('prompt_' + hint.key) : hint.fallback;
    const btn = createHintChip(displayText, () => handleHintClick(promptVal), i);
    btn.dataset.prompt = hint.key;

    const cleanText = displayText.replace(/^([\u{1F300}-\u{1F9FF}\u{2600}-\u{27BF}]|💡|✍️|📄|🌐)\s*/u, '');
    const iconHtml = getQuestionIcon(displayText);
    btn.innerHTML = `${iconHtml}<span data-i18n="${hint.i18nKey}">${sanitizeHtml(cleanText)}</span>`;
    container.appendChild(btn);
  });
}

export function updateWelcomeHints(immediate = false) {
  const welcomeScreen = $('welcome-screen');
  if (!welcomeScreen || welcomeScreen.classList.contains('hidden')) return;

  const welcomeHintsContainer = document.querySelector('.welcome-hints');
  if (!welcomeHintsContainer) return;

  const performUpdate = () => {
    const activeCategory = $<HTMLSelectElement>('conversation-session-category')?.value || (state.currentUser ? state.currentUser.category : '');
    const categoryData = state.categories?.[activeCategory || ''];
    const questions = getSuggestedQuestions(categoryData);

    renderHints(welcomeHintsContainer as HTMLElement, questions);
    welcomeHintsContainer.classList.remove('updating');
  };

  if (state.welcomeHintsTimeout) {
    clearTimeout(state.welcomeHintsTimeout);
    state.welcomeHintsTimeout = null;
  }

  if (immediate || welcomeHintsContainer.children.length === 0) {
    performUpdate();
  } else {
    welcomeHintsContainer.classList.add('updating');
    state.welcomeHintsTimeout = setTimeout(() => {
      performUpdate();
      state.welcomeHintsTimeout = null;
    }, 250);
  }
}

/** Resize category select width to fit selected option text */
export function resizeCategorySelect(): void {
  const select = $<HTMLSelectElement>('conversation-session-category');
  if (!select) return;
  const option = select.options[select.selectedIndex];
  if (!option) return;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.font = window.getComputedStyle(select).font || '500 0.8rem var(--font)';
  const textWidth = ctx.measureText(option.textContent || '').width;
  // 26px accounts for dropdown arrow (20px) + padding (6px)
  select.style.setProperty('--cat-width', `${Math.ceil(textWidth) + 26}px`);
}
